"use client"

import { buildScrubbedError, scrubProperties } from "./pii"
import { isPostHogTelemetryEnabled } from "./telemetry-config"

interface ClientPostHog {
  captureException?: (error: unknown, properties?: Record<string, unknown>) => void
  capture?: (event: string, properties?: Record<string, unknown>) => void
}

interface CaptureClientContext {
  scope: string
  extra?: Record<string, unknown>
}

function getClientPostHog(): ClientPostHog | null {
  if (!isPostHogTelemetryEnabled()) return null
  if (typeof window === "undefined") return null
  const ph = (window as unknown as { posthog?: ClientPostHog }).posthog
  if (!ph) return null
  return ph
}

export function captureClientEvent(
  event: string,
  properties?: Record<string, unknown>
): void {
  try {
    const ph = getClientPostHog()
    if (!ph || typeof ph.capture !== "function") return
    ph.capture(event, properties ? scrubProperties(properties) : undefined)
  } catch {
    // Telemetry must not interrupt recovery.
  }
}

export function captureClientException(
  err: unknown,
  context: CaptureClientContext
): void {
  try {
    const ph = getClientPostHog()
    if (!ph) return

    // posthog-js builds `$exception_list` from the Error's own message/stack.
    // Hand it a pre-scrubbed Error so secrets/PII in the raw message/stack
    // never leave the browser.
    const scrubbed = buildScrubbedError(err)

    const environment =
      process.env.NEXT_PUBLIC_IS_PREVIEW === "true"
        ? "preview"
        : process.env.NODE_ENV === "production"
          ? "production"
          : "development"

    const props = {
      $exception_source: "client",
      scope: context.scope,
      route_path:
        typeof window !== "undefined" ? window.location.pathname : undefined,
      environment,
      git_commit: process.env.NEXT_PUBLIC_COMMIT_SHA ?? "unknown",
      ...(context.extra ? scrubProperties(context.extra) : {}),
    }

    if (typeof ph.captureException === "function") {
      ph.captureException(scrubbed, props)
    } else if (typeof ph.capture === "function") {
      ph.capture("$exception", {
        $exception_type: scrubbed.name,
        $exception_message: scrubbed.message,
        $exception_stack_trace_raw: scrubbed.stack,
        ...props,
      })
    }
  } catch {
    // never let error reporting throw
  }
}
